// src/admin/AdminProducts.jsx - Full Screen Modern Design
import React, { useState, useEffect } from 'react';
import { productsAPI, categoriesAPI } from '../../services/api';
import {
  Plus,
  Edit3,
  Trash2,
  Package,
  Search,
  X,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Check,
  Grid3X3,
  List,
  Upload,
  Image as ImageIcon,
  RotateCcw,
  DollarSign,
  Tag,
  Layers,
  FileText,
  Settings2,
  Save,
  Archive,
  Filter,
  MoreVertical,
  Eye,
  Copy,
  TrendingUp,
  Box,
  AlertTriangle
} from 'lucide-react';
import './css/AdminProducts.css';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [trashProducts, setTrashProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    regularPrice: '',
    salePrice: '',
    categoryId: '',
    shortDescription: '',
    longDescription: '',
    stock: '',
    lowStockThreshold: '10',
    enableOptions: false,
    options: [],
    seo_title: '',
    seo_description: '',
    seo_keywords: ''
  });

  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [tempOptions, setTempOptions] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (showTrash) fetchTrash();
  }, [showTrash]);

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => setNotification({ show: false, type: '', message: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
  };

  const fetchProducts = async () => {
    setFetchLoading(true);
    try {
      const { data } = await productsAPI.getAll();
      setProducts(data);
    } catch (err) {
      showNotification('error', 'Failed to fetch products');
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchTrash = async () => {
    try {
      const { data } = await productsAPI.getTrash();
      setTrashProducts(data);
    } catch (err) {
      showNotification('error', 'Failed to load trash');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await categoriesAPI.getAll();
      setCategories(data);
    } catch (err) {
      showNotification('error', 'Failed to load categories');
    }
  };

  const fetchProductForEdit = async (id) => {
    try {
      const { data } = await productsAPI.getById(id);
      const normalizedOptions = (data.options || []).map(opt => ({
        ...opt,
        is_required: !!opt.is_required
      }));

      setFormData({
        name: data.name || '',
        regularPrice: data.regularPrice || '',
        salePrice: data.salePrice || '',
        categoryId: data.categoryId || '',
        shortDescription: data.shortDescription || '',
        longDescription: data.longDescription || '',
        stock: data.stock || '',
        lowStockThreshold: data.lowStockThreshold || '10',
        enableOptions: !!(data.options && data.options.length > 0),
        options: normalizedOptions,
        seo_title: data.seo_title || '',
        seo_description: data.seo_description || '',
        seo_keywords: data.seo_keywords || ''
      });
      setTempOptions(normalizedOptions);
      setImagePreview(data.imageUrl || '');
      setImageFile(null);
      setGalleryPreviews(data.galleryImages ? JSON.parse(data.galleryImages) : []);
      setGalleryFiles([]);
      setEditingId(id);
      setShowForm(true);
      setActiveTab('basic');
    } catch (err) {
      showNotification('error', 'Failed to load product data');
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '/placeholder-image.jpg';
    return imageUrl.startsWith('http') ? imageUrl : `http://localhost:5000/images/${imageUrl}`;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      regularPrice: '',
      salePrice: '',
      categoryId: '',
      shortDescription: '',
      longDescription: '',
      stock: '',
      lowStockThreshold: '10',
      enableOptions: false,
      options: [],
      seo_title: '',
      seo_description: '',
      seo_keywords: ''
    });
    setTempOptions([]);
    setImagePreview('');
    setImageFile(null);
    setGalleryPreviews([]);
    setGalleryFiles([]);
    setEditingId(null);
    setShowForm(false);
    setActiveTab('basic');
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showNotification('error', 'Image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageFile(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    if (galleryPreviews.length + files.length > 10) {
      showNotification('error', 'Maximum 10 gallery images allowed');
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        showNotification('error', 'Each image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setGalleryPreviews(prev => [...prev, reader.result]);
        setGalleryFiles(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
    setGalleryFiles(prev => prev.filter((_, i) => i !== index));
  };

  const addOptionGroup = (groupName) => {
    if (!groupName.trim()) return;
    setTempOptions(prev => [...prev, {
      name: groupName.trim(),
      is_required: true,
      values: []
    }]);
  };

  const deleteGroup = (index) => {
    setTempOptions(prev => prev.filter((_, i) => i !== index));
  };

  const addOptionValue = (groupIndex, valueName, extraPrice) => {
    if (!valueName.trim()) return;
    setTempOptions(prev => prev.map((g, i) =>
      i === groupIndex ? { ...g, values: [...g.values, { value: valueName.trim(), extra_price: parseFloat(extraPrice) || 0 }] } : g
    ));
  };

  const deleteValue = (groupIndex, valueIndex) => {
    setTempOptions(prev => prev.map((g, i) =>
      i === groupIndex ? { ...g, values: g.values.filter((_, j) => j !== valueIndex) } : g
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.regularPrice || !formData.categoryId || !formData.stock) {
      showNotification('error', 'Please fill in all required fields');
      return;
    }

    const selectedCat = categories.find(c => String(c.id) === String(formData.categoryId));
    
    setLoading(true);
    try {
      const optionsForBackend = tempOptions.map(opt => ({
        ...opt,
        is_required: opt.is_required === true ? 1 : 0
      }));

      const payload = {
        ...formData,
        regularPrice: parseFloat(formData.regularPrice),
        salePrice: formData.salePrice ? parseFloat(formData.salePrice) : null,
        categoryId: parseInt(formData.categoryId),
        stock: parseInt(formData.stock),
        lowStockThreshold: parseInt(formData.lowStockThreshold),
        options: formData.enableOptions ? optionsForBackend : [],
        image: imageFile,
        galleryImages: galleryPreviews,
        seo_title: formData.seo_title || `${formData.name} | Reyza`,
        seo_description: formData.seo_description || formData.shortDescription,
        seo_keywords: formData.seo_keywords || `${formData.name}${selectedCat ? `, ${selectedCat.name}` : ''}`
      };

      if (editingId) {
        await productsAPI.update(editingId, payload);
        showNotification('success', 'Product updated successfully!');
      } else {
        await productsAPI.create(payload);
        showNotification('success', 'Product created successfully!');
      }

      resetForm();
      fetchProducts();
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Move this product to trash?')) return;
    try {
      await productsAPI.delete(id);
      setProducts(prev => prev.filter(p => p.id !== id));
      showNotification('success', 'Product moved to trash');
    } catch (err) {
      showNotification('error', 'Delete failed');
    }
  };

  const handleRestore = async (id) => {
    try {
      await productsAPI.restore(id);
      showNotification('success', 'Product restored!');
      fetchTrash();
      fetchProducts();
    } catch (err) {
      showNotification('error', 'Restore failed');
    }
  };

  const handlePurge = async (id) => {
    if (!window.confirm('Permanently delete this product? This cannot be undone.')) return;
    try {
      await productsAPI.purge(id);
      showNotification('success', 'Permanently deleted');
      fetchTrash();
    } catch (err) {
      showNotification('error', 'Purge failed');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.categoryName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || String(p.categoryId) === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Stats
  const totalProducts = products.length;
  const lowStockCount = products.filter(p => p.stock <= p.lowStockThreshold).length;
  const onSaleCount = products.filter(p => p.salePrice).length;

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Package },
    { id: 'images', label: 'Images', icon: ImageIcon },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'options', label: 'Options', icon: Layers },
    { id: 'seo', label: 'SEO', icon: FileText },
  ];

  if (fetchLoading) {
    return (
      <div className="ap-loading">
        <div className="ap-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="ap-wrapper">
      {/* Notification */}
      {notification.show && (
        <div className={`ap-notification ${notification.type}`}>
          {notification.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ show: false, type: '', message: '' })}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Sidebar */}
      <aside className="ap-sidebar">
        <div className="ap-sidebar-header">
          <h2>Products</h2>
          <button
            className="ap-btn-add"
            onClick={() => { resetForm(); setShowForm(true); }}
          >
            <Plus size={20} />
          </button>
        </div>

        {/* Stats */}
        <div className="ap-stats">
          <div className="ap-stat-card">
            <div className="ap-stat-icon blue">
              <Box size={20} />
            </div>
            <div className="ap-stat-info">
              <span className="ap-stat-value">{totalProducts}</span>
              <span className="ap-stat-label">Total Products</span>
            </div>
          </div>
          <div className="ap-stat-card">
            <div className="ap-stat-icon orange">
              <AlertTriangle size={20} />
            </div>
            <div className="ap-stat-info">
              <span className="ap-stat-value">{lowStockCount}</span>
              <span className="ap-stat-label">Low Stock</span>
            </div>
          </div>
          <div className="ap-stat-card">
            <div className="ap-stat-icon green">
              <TrendingUp size={20} />
            </div>
            <div className="ap-stat-info">
              <span className="ap-stat-value">{onSaleCount}</span>
              <span className="ap-stat-label">On Sale</span>
            </div>
          </div>
        </div>

        {/* Categories Filter */}
        <div className="ap-filter-section">
          <h3>Categories</h3>
          <div className="ap-category-list">
            <button
              className={`ap-category-item ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              <span>All Products</span>
              <span className="ap-category-count">{products.length}</span>
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`ap-category-item ${selectedCategory === String(cat.id) ? 'active' : ''}`}
                onClick={() => setSelectedCategory(String(cat.id))}
              >
                <span>{cat.name}</span>
                <span className="ap-category-count">
                  {products.filter(p => p.categoryId === cat.id).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="ap-quick-actions">
          <button
            className={`ap-quick-btn ${showTrash ? 'active' : ''}`}
            onClick={() => setShowTrash(!showTrash)}
          >
            <Archive size={18} />
            <span>Trash</span>
            {trashProducts.length > 0 && (
              <span className="ap-badge-count">{trashProducts.length}</span>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ap-main">
        {/* Top Bar */}
        <div className="ap-topbar">
          <div className="ap-search-wrapper">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="ap-search-clear" onClick={() => setSearchTerm('')}>
                <X size={16} />
              </button>
            )}
          </div>

          <div className="ap-topbar-actions">
            <div className="ap-view-toggle">
              <button
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <Grid3X3 size={18} />
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <List size={18} />
              </button>
            </div>

            <button className="ap-btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
              <Plus size={18} />
              <span>Add Product</span>
            </button>
          </div>
        </div>

        {/* Page Title */}
        <div className="ap-page-header">
          <div>
            <h1>{showTrash ? 'Trash' : 'All Products'}</h1>
            <p>{showTrash ? trashProducts.length : filteredProducts.length} {showTrash ? 'deleted items' : 'products found'}</p>
          </div>
          {showTrash && (
            <button className="ap-btn-outline" onClick={() => setShowTrash(false)}>
              <Package size={18} />
              Back to Products
            </button>
          )}
        </div>

        {/* Products Content */}
        <div className="ap-content">
          {showTrash ? (
            trashProducts.length === 0 ? (
              <div className="ap-empty">
                <Archive size={64} />
                <h3>Trash is empty</h3>
                <p>No deleted products</p>
                <button className="ap-btn-outline" onClick={() => setShowTrash(false)}>
                  <Package size={18} />
                  View Products
                </button>
              </div>
            ) : (
              <div className={`ap-products ap-${viewMode}`}>
                {trashProducts.map(product => (
                  <div key={product.id} className="ap-card trashed">
                    <div className="ap-card-image">
                      <img
                        src={getImageUrl(product.imageUrl)}
                        alt={product.name}
                        onError={(e) => e.target.src = '/placeholder-image.jpg'}
                      />
                      <div className="ap-card-badge danger">Deleted</div>
                    </div>
                    <div className="ap-card-body">
                      <span className="ap-card-category">{product.categoryName}</span>
                      <h3 className="ap-card-title">{product.name}</h3>
                      <div className="ap-card-price">
                        <span className="ap-price-current">DT {product.salePrice || product.regularPrice}</span>
                      </div>
                      <div className="ap-card-actions">
                        <button onClick={() => handleRestore(product.id)} className="ap-btn-success">
                          <RotateCcw size={16} />
                          Restore
                        </button>
                        <button onClick={() => handlePurge(product.id)} className="ap-btn-danger">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : filteredProducts.length === 0 ? (
            <div className="ap-empty">
              {searchTerm ? (
                <>
                  <Search size={64} />
                  <h3>No results found</h3>
                  <p>Try different search terms or clear the filter</p>
                  <button className="ap-btn-outline" onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}>
                    Clear Filters
                  </button>
                </>
              ) : (
                <>
                  <Package size={64} />
                  <h3>No products yet</h3>
                  <p>Start by adding your first product</p>
                  <button className="ap-btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
                    <Plus size={18} />
                    Add Product
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className={`ap-products ap-${viewMode}`}>
              {filteredProducts.map(product => (
                <div key={product.id} className="ap-card">
                  <div className="ap-card-image">
                    <img
                      src={getImageUrl(product.imageUrl)}
                      alt={product.name}
                      onError={(e) => e.target.src = '/placeholder-image.jpg'}
                    />
                    {product.salePrice && (
                      <div className="ap-card-badge sale">
                        {Math.round((1 - product.salePrice / product.regularPrice) * 100)}% OFF
                      </div>
                    )}
                    {product.stock <= product.lowStockThreshold && (
                      <div className="ap-card-badge warning">Low Stock</div>
                    )}
                    <div className="ap-card-overlay">
                      <button onClick={() => fetchProductForEdit(product.id)} title="Edit">
                        <Edit3 size={18} />
                      </button>
                      <button onClick={() => handleDelete(product.id)} title="Delete">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="ap-card-body">
                    <span className="ap-card-category">{product.categoryName}</span>
                    <h3 className="ap-card-title">{product.name}</h3>
                    <div className="ap-card-price">
                      <span className="ap-price-current">DT {product.salePrice || product.regularPrice}</span>
                      {product.salePrice && (
                        <span className="ap-price-original">DT {product.regularPrice}</span>
                      )}
                    </div>
                    <div className="ap-card-meta">
                      <span className={product.stock <= product.lowStockThreshold ? 'low' : ''}>
                        <Box size={14} />
                        {product.stock} in stock
                      </span>
                      {product.options && product.options.length > 0 && (
                        <span>
                          <Layers size={14} />
                          {product.options.length} options
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Product Form Modal */}
      {showForm && (
        <div className="ap-modal-overlay" onClick={() => resetForm()}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <div>
                <h2>{editingId ? 'Edit Product' : 'New Product'}</h2>
                <p>Fill in the product details below</p>
              </div>
              <button className="ap-modal-close" onClick={resetForm}>
                <X size={24} />
              </button>
            </div>

            <div className="ap-modal-tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`ap-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="ap-form">
              <div className="ap-form-content">
                {/* Basic Info Tab */}
                {activeTab === 'basic' && (
                  <div className="ap-tab-content">
                    <div className="ap-field">
                      <label>Product Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter product name"
                        required
                      />
                    </div>

                    <div className="ap-field">
                      <label>Category <span className="required">*</span></label>
                      <select
                        name="categoryId"
                        value={formData.categoryId}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select category</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="ap-field">
                      <label>Short Description</label>
                      <textarea
                        name="shortDescription"
                        value={formData.shortDescription}
                        onChange={handleChange}
                        placeholder="Brief product summary"
                        rows={3}
                      />
                    </div>

                    <div className="ap-field">
                      <label>Full Description</label>
                      <textarea
                        name="longDescription"
                        value={formData.longDescription}
                        onChange={handleChange}
                        placeholder="Detailed product description"
                        rows={6}
                      />
                    </div>
                  </div>
                )}

                {/* Images Tab */}
                {activeTab === 'images' && (
                  <div className="ap-tab-content">
                    <div className="ap-field">
                      <label>Main Image</label>
                      <div className="ap-image-upload">
                        {imagePreview ? (
                          <div className="ap-image-preview">
                            <img src={imagePreview} alt="Preview" />
                            <button
                              type="button"
                              onClick={() => { setImagePreview(''); setImageFile(null); }}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ) : (
                          <label className="ap-upload-zone">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleMainImageChange}
                            />
                            <Upload size={40} />
                            <span>Click to upload image</span>
                            <small>PNG, JPG up to 5MB</small>
                          </label>
                        )}
                      </div>
                    </div>

                    <div className="ap-field">
                      <label>Gallery Images <small>(up to 10 images)</small></label>
                      <div className="ap-gallery-grid">
                        {galleryPreviews.map((preview, index) => (
                          <div key={index} className="ap-gallery-item">
                            <img src={preview} alt={`Gallery ${index + 1}`} />
                            <button type="button" onClick={() => removeGalleryImage(index)}>
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                        {galleryPreviews.length < 10 && (
                          <label className="ap-gallery-add">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleGalleryChange}
                            />
                            <Plus size={24} />
                            <span>Add</span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pricing Tab */}
                {activeTab === 'pricing' && (
                  <div className="ap-tab-content">
                    <div className="ap-field-row">
                      <div className="ap-field">
                        <label>Regular Price <span className="required">*</span></label>
                        <div className="ap-input-group">
                          <span className="ap-input-addon">DT</span>
                          <input
                            type="number"
                            name="regularPrice"
                            value={formData.regularPrice}
                            onChange={handleChange}
                            placeholder="0.00"
                            step="0.01"
                            required
                          />
                        </div>
                      </div>

                      <div className="ap-field">
                        <label>Sale Price <small>(optional)</small></label>
                        <div className="ap-input-group">
                          <span className="ap-input-addon">DT</span>
                          <input
                            type="number"
                            name="salePrice"
                            value={formData.salePrice}
                            onChange={handleChange}
                            placeholder="0.00"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="ap-field-row">
                      <div className="ap-field">
                        <label>Stock Quantity <span className="required">*</span></label>
                        <input
                          type="number"
                          name="stock"
                          value={formData.stock}
                          onChange={handleChange}
                          placeholder="0"
                          min="0"
                          required
                        />
                      </div>

                      <div className="ap-field">
                        <label>Low Stock Alert</label>
                        <input
                          type="number"
                          name="lowStockThreshold"
                          value={formData.lowStockThreshold}
                          onChange={handleChange}
                          placeholder="10"
                          min="0"
                        />
                        <small>Alert when stock falls below this number</small>
                      </div>
                    </div>
                  </div>
                )}

                {/* Options Tab */}
                {activeTab === 'options' && (
                  <div className="ap-tab-content">
                    <label className="ap-switch">
                      <input
                        type="checkbox"
                        checked={formData.enableOptions}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, enableOptions: e.target.checked }));
                          if (!e.target.checked) setTempOptions([]);
                        }}
                      />
                      <span className="ap-switch-slider"></span>
                      <span className="ap-switch-label">Enable product options (size, color, etc.)</span>
                    </label>

                    {formData.enableOptions && (
                      <div className="ap-options-editor">
                        {tempOptions.length === 0 ? (
                          <div className="ap-empty-options">
                            <Layers size={48} />
                            <p>No options added yet</p>
                            <small>Add option groups like Size, Color, Material, etc.</small>
                          </div>
                        ) : (
                          <div className="ap-options-list">
                            {tempOptions.map((group, gIndex) => (
                              <div key={gIndex} className="ap-option-group">
                                <div className="ap-option-header">
                                  <input
                                    type="text"
                                    value={group.name}
                                    onChange={(e) => setTempOptions(prev => prev.map((g, i) =>
                                      i === gIndex ? { ...g, name: e.target.value } : g
                                    ))}
                                    placeholder="Option name (e.g., Size)"
                                  />
                                  <label className="ap-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={group.is_required}
                                      onChange={(e) => setTempOptions(prev => prev.map((g, i) =>
                                        i === gIndex ? { ...g, is_required: e.target.checked } : g
                                      ))}
                                    />
                                    <span>Required</span>
                                  </label>
                                  <button
                                    type="button"
                                    className="ap-btn-icon-sm danger"
                                    onClick={() => deleteGroup(gIndex)}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>

                                <div className="ap-option-values">
                                  {group.values.map((val, vIndex) => (
                                    <div key={vIndex} className="ap-value-row">
                                      <input
                                        type="text"
                                        value={val.value}
                                        onChange={(e) => setTempOptions(prev => prev.map((g, gi) =>
                                          gi === gIndex ? {
                                            ...g,
                                            values: g.values.map((v, vi) =>
                                              vi === vIndex ? { ...v, value: e.target.value } : v
                                            )
                                          } : g
                                        ))}
                                        placeholder="Value name"
                                      />
                                      <div className="ap-input-group small">
                                        <span className="ap-input-addon">+DT</span>
                                        <input
                                          type="number"
                                          value={val.extra_price}
                                          onChange={(e) => setTempOptions(prev => prev.map((g, gi) =>
                                            gi === gIndex ? {
                                              ...g,
                                              values: g.values.map((v, vi) =>
                                                vi === vIndex ? { ...v, extra_price: parseFloat(e.target.value) || 0 } : v
                                              )
                                            } : g
                                          ))}
                                          step="0.01"
                                          placeholder="0"
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        className="ap-btn-icon-sm"
                                        onClick={() => deleteValue(gIndex, vIndex)}
                                      >
                                        <X size={16} />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    className="ap-btn-ghost"
                                    onClick={() => addOptionValue(gIndex, '', 0)}
                                  >
                                    <Plus size={16} />
                                    Add Value
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="ap-add-group">
                          <input
                            type="text"
                            placeholder="New option group name (e.g., Size, Color)"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addOptionGroup(e.target.value);
                                e.target.value = '';
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="ap-btn-primary"
                            onClick={(e) => {
                              const input = e.target.previousElementSibling;
                              addOptionGroup(input.value);
                              input.value = '';
                            }}
                          >
                            <Plus size={18} />
                            Add Group
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* SEO Tab */}
                {activeTab === 'seo' && (
                  <div className="ap-tab-content">
                    <div className="ap-seo-preview">
                      <span className="ap-seo-url">yourstore.com/products/{formData.name.toLowerCase().replace(/\s+/g, '-') || 'product-name'}</span>
                      <h4>{formData.seo_title || formData.name || 'Product Title'}</h4>
                      <p>{formData.seo_description || formData.shortDescription || 'Product description will appear here...'}</p>
                    </div>

                    <div className="ap-field">
                      <label>SEO Title</label>
                      <input
                        type="text"
                        name="seo_title"
                        value={formData.seo_title}
                        onChange={handleChange}
                        placeholder="Product name | Your Store"
                        maxLength={70}
                      />
                      <div className="ap-field-footer">
                        <small>Recommended: 50-60 characters</small>
                        <span className={formData.seo_title.length > 60 ? 'warning' : ''}>
                          {formData.seo_title.length}/70
                        </span>
                      </div>
                    </div>

                    <div className="ap-field">
                      <label>Meta Description</label>
                      <textarea
                        name="seo_description"
                        value={formData.seo_description}
                        onChange={handleChange}
                        placeholder="Brief description for search engines"
                        rows={3}
                        maxLength={160}
                      />
                      <div className="ap-field-footer">
                        <small>Recommended: 120-160 characters</small>
                        <span className={formData.seo_description.length > 160 ? 'warning' : ''}>
                          {formData.seo_description.length}/160
                        </span>
                      </div>
                    </div>

                    <div className="ap-field">
                      <label>Keywords</label>
                      <input
                        type="text"
                        name="seo_keywords"
                        value={formData.seo_keywords}
                        onChange={handleChange}
                        placeholder="keyword1, keyword2, keyword3"
                      />
                      <small>Separate keywords with commas</small>
                    </div>
                  </div>
                )}
              </div>

              <div className="ap-form-footer">
                <button type="button" className="ap-btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="ap-btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="ap-btn-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingId ? 'Update Product' : 'Create Product'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;